import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useSearchParams } from 'react-router-dom'
import { ApexOptions } from 'apexcharts'
import dayjs from 'dayjs'
import { getStore } from 'services'
import { useLoading, useMachineInfor } from 'stores'
import { HitcountChartType, StoreType } from 'types'
import { ROUTE_HOME } from 'constant'

export const useStore = () => {
  const setLoading = useLoading(state => state.setLoading)
  const setMachineInfor = useMachineInfor(state => state.setMachineInfor)

  const { pathname, search } = useLocation()
  const [searchParams] = useSearchParams()
  const storeID = searchParams.get('store_id')
  const machineID = searchParams.get('machine_id')
  const unitNo = searchParams.get('unit_no')
  const hitDate = searchParams.get('hit_date')

  const xaxisWrapRef = useRef<HTMLDivElement>(null)
  const countInterValRef = useRef<number>(0)

  const [data, setData] = useState<StoreType>()
  const [chartWidth, setChartWidth] = useState(0)
  const [convertData, setConvertData] = useState<number[][]>([])
  const [renderEndChart, setRenderEndChart] = useState<boolean>(false)
  const [visibleLabelChart, setVisibleLabelChart] = useState<boolean>(false)
  const [showText, setShowText] = useState<boolean>(false)

  useEffect(() => {
    ;(async () => {
      if (storeID && machineID && unitNo) {
        setLoading(true)
        try {
          const {
            data: { data },
          } = await getStore(
            machineID,
            storeID,
            unitNo,
            hitDate || dayjs().format('YYYY-MM-DD'),
          )
          setData(data)
          setMachineInfor(data.machine_info)
          setLoading(false)
        } catch (error) {
          setLoading(false)
          console.error(error)
        }
      }
    })()
  }, [hitDate, machineID, setLoading, setMachineInfor, storeID, unitNo])

  useEffect(() => {
    if (xaxisWrapRef.current) {
      setChartWidth(xaxisWrapRef.current.offsetWidth)
    }
  }, [])

  useEffect(() => {
    if (!data?.hitcount_chart_data) return
    const TOTAL_TIME = 1000

    const dataConvert: number[][] = []
    const startTime = new Date('01/01/2014').getTime()
    const totalTime = 86400000 * 3

    if (
      (data?.hitcount_chart_data && data?.hitcount_chart_data?.length === 0) ||
      (data?.hitcount_chart_data.length <= 3 &&
        data?.hitcount_chart_data.every(item => item.diff === 0)) ||
      data?.hitcount_chart_data === null ||
      data?.hitcount_chart_data === undefined
    ) {
      const fakeData = [...sampleData.map(d => d.diff)]
      for (let i = 0; i < fakeData.length; i++) {
        const time = startTime + (totalTime / (fakeData.length - 1)) * i
        const innerArr = [time, fakeData[i]]
        dataConvert.push(innerArr)
      }
      setShowText(true)
    } else {
      const rawData = data?.hitcount_chart_data.map(d => d.diff) || []
      const duplicates: HitcountChartType[] = []
      data?.hitcount_chart_data.forEach(item => {
        if (!duplicates.find(dupItem => dupItem.hit_date === item.hit_date)) {
          duplicates.push(item)
        }
      })

      if (duplicates.length === 1) {
        const fakeData = [...sampleData.map(d => d.diff), ...rawData]
        for (let i = 0; i < fakeData.length; i++) {
          const time = startTime + (totalTime / (fakeData.length - 1)) * i
          const innerArr = [time, fakeData[i]]
          dataConvert.push(innerArr)
        }
      } else {
        for (let i = 0; i < rawData.length; i++) {
          const time = startTime + (totalTime / (rawData.length - 1)) * i
          const innerArr = [time, rawData[i]]
          dataConvert.push(innerArr)
        }
      }
    }

    let intervalID: NodeJS.Timer
    if (dataConvert.length > 0) {
      const step = TOTAL_TIME / dataConvert.length
      intervalID = setInterval(() => {
        const index = countInterValRef.current
        const item = dataConvert[index]
        if (!item) clearInterval(intervalID)
        if (item !== undefined) {
          setConvertData(prev => {
            const newArr = [...prev]
            newArr.push(item)
            return newArr
          })
          countInterValRef.current = index + 1
        }
      }, step)
    }
    return () => {
      clearInterval(intervalID)
    }
  }, [data?.hitcount_chart_data])

  useEffect(() => {
    let timeoutID: NodeJS.Timer
    if (renderEndChart) {
      timeoutID = setTimeout(() => {
        setVisibleLabelChart(true)
      }, 1000 * 2)
    }

    return () => {
      clearTimeout(timeoutID)
    }
  }, [renderEndChart])

  const optionsChart: ApexOptions = useMemo(() => {
    let minChart, maxChart

    if (
      (data?.hitcount_chart_data && data?.hitcount_chart_data?.length === 0) ||
      (data?.hitcount_chart_data &&
        data?.hitcount_chart_data.length >= 2 &&
        data?.hitcount_chart_data.every(item => item.diff === 0)) ||
      data?.hitcount_chart_data === null ||
      data?.hitcount_chart_data === undefined
    ) {
      maxChart = 10000
      minChart = -6000
    } else {
      const rawData = data?.hitcount_chart_data.map(d => d.diff) || []
      const maxData = Math.max(...rawData)
      const minData = Math.min(...rawData)
      const lastData = rawData[rawData.length - 1]
      const range = Math.max((maxData - lastData) / 6, (lastData - minData) / 4)
      // if (range < 300) {
      //   maxChart = roundToNearestThousand(range * 6 * 6 + lastData)
      //   minChart = roundToNearestThousand(lastData - range * 6 * 4)
      // } else {
      const roundMax = roundToNearestThousand(range * 6 + lastData)
      const roundMin = roundToNearestThousand(lastData - range * 4)
      if ((roundMax - lastData) / (roundMax - roundMin) < 0.55) {
        maxChart = roundMax + 1000
        minChart = roundMin
      } else if ((lastData - roundMin) / (roundMax - roundMin) > 0.45) {
        maxChart = roundMax
        minChart = roundMin - 1000
      } else {
        maxChart = roundMax
        minChart = roundMin
      }
      // }
      // maxChart = (range < 300 ? range * 6 : range) * 6 + lastData
      // minChart = lastData - (range < 300 ? range * 6 : range) * 4
    }

    return {
      chart: {
        stacked: false,
        toolbar: { show: false },
        zoom: { enabled: false },
        animations: {
          enabled: true,
          speed: 50,
          easing: 'linear',
          dynamicAnimation: {
            enabled: true,
            speed: 50,
          },
          animateGradually: {
            enabled: true,
            delay: 50,
          },
        },
        events: {
          animationEnd: chart => {
            if (
              ((data?.hitcount_chart_data &&
                data?.hitcount_chart_data?.length === 0) ||
                data?.hitcount_chart_data === null ||
                data?.hitcount_chart_data === undefined) &&
              chart.data.twoDSeries.length === sampleData.length
            ) {
              setRenderEndChart(true)
            }
            if (
              data?.hitcount_chart_data &&
              data?.hitcount_chart_data.length > 0 &&
              data?.hitcount_chart_data.length === chart.data.twoDSeries.length
            ) {
              setRenderEndChart(true)
            }
          },
        },
      },
      annotations: {
        yaxis: [
          {
            y: 0,
            borderColor: '#F60A0A',
            borderWidth: 0.2,
            strokeDashArray: 0,
          },
        ],
      },
      grid: {
        show: false,
        borderColor: '#F0F0F0',
        position: 'back',
        xaxis: {
          lines: {
            show: false,
          },
        },
        yaxis: {
          lines: {
            show: true,
          },
        },
      },
      stroke: {
        curve: ['smooth'],
        width: [1.8],
      },
      fill: {
        type: ['gradient'],
        opacity: [0.3],
        gradient: {
          shadeIntensity: 1,
          inverseColors: false,
          opacityFrom: 0.8,
          opacityTo: 0.1,
          stops: [0, 100],
        },
      },
      dataLabels: {
        enabled: false,
      },
      markers: {
        size: 0,
      },
      yaxis: {
        max: maxChart,
        min: minChart,
        tickAmount: 1,
        labels: {
          minWidth: 25,
          style: {
            colors: '#A3A3A3',
            fontFamily: 'Roboto',
            fontSize: '7.27px',
            fontWeight: '600',
          },
          offsetX: 0,
          formatter: function (val) {
            return val.toFixed(0)
          },
        },
        axisBorder: {
          show: true,
        },
        axisTicks: {
          show: false,
        },
      },
      xaxis: {
        type: 'datetime',
        min: new Date('01/01/2014').getTime(),
        max: new Date('01/06/2014').getTime(),
        labels: {
          show: false,
        },
        axisBorder: {
          show: false,
        },
        axisTicks: {
          show: false,
        },
      },
      tooltip: {
        enabled: false,
      },
      legend: {
        show: false,
      },
    }
  }, [data?.hitcount_chart_data])

  const series: ApexAxisChartSeries | ApexNonAxisChartSeries = useMemo(() => {
    return [
      {
        name: '1',
        color: '#FFFFFF',
        data: convertData,
      },
    ]
  }, [convertData])

  const xaxisPosition = useMemo(() => {
    const positions = []
    const duplicates: HitcountChartType[] = []
    data?.hitcount_chart_data.forEach(item => {
      if (!duplicates.find(dupItem => dupItem.hit_date === item.hit_date)) {
        duplicates.push(item)
      }
    })

    if (duplicates.length === 0) {
      const item = {
        diff: 0,
        hit_date: dayjs(hitDate || undefined)
          .add(isAfterOrEqual21() ? 1 : 0, 'day')
          .format('YYYY-MM-DD'),
      }
      const fakeData = [...sampleData.map(d => d), item]
      const step = chartWidth / (fakeData.length || 1)
      positions.push({
        month: dayjs(item.hit_date).format('MM'),
        day: dayjs(item.hit_date).format('DD'),
        position: (fakeData?.indexOf(item) + 1) * step - 16,
      })
    } else if (duplicates.length === 1) {
      const fakeData = [
        ...sampleData.map(d => d),
        ...(data?.hitcount_chart_data || []),
      ]
      const step = chartWidth / (fakeData.length || 1)
      if (fakeData?.indexOf(duplicates[0])) {
        positions.push({
          month: dayjs(duplicates[0].hit_date).format('MM'),
          day: dayjs(duplicates[0].hit_date).format('DD'),
          position: (fakeData?.indexOf(duplicates[0]) + 1) * step - 16,
        })
      }
    } else {
      const step = chartWidth / (data?.hitcount_chart_data.length || 1)
      for (let i = 0; i < duplicates.length; i++) {
        if (data?.hitcount_chart_data?.indexOf(duplicates[i])) {
          positions.push({
            month: dayjs(duplicates[i].hit_date).format('MM'),
            day: dayjs(duplicates[i].hit_date).format('DD'),
            position:
              (data?.hitcount_chart_data?.indexOf(duplicates[i]) + 1) * step -
              16,
          })
        }
      }
    }
    return positions
  }, [chartWidth, data?.hitcount_chart_data, hitDate])

  const forecast = useMemo(() => {
    if (data?.distance_rate) {
      return {
        down: roundTo(data.distance_rate.down * 100),
        flat: roundTo(data.distance_rate.flat * 100),
        up: roundTo(data.distance_rate.up * 100),
      }
    } else {
      return {
        down: 0,
        flat: 0,
        up: 0,
      }
    }
  }, [data?.distance_rate])

  return {
    series,
    optionsChart,
    machineInfo: data?.machine_info,
    xaxisPosition,
    xaxisWrapRef,
    forecast,
    renderEndChart,
    visibleLabelChart,
    enoughTarget: data?.enough_target_data,
    showText,
    params: {
      storeID,
      machineID,
      unitNo,
      hitDate,
      calcNum: data?.calc_num,
    },
    switchUi: pathname === ROUTE_HOME && search === '',
  }
}

const sampleData = Array.from({ length: 5 }, () => ({
  diff: 0,
  hit_date: '2014-01-01',
}))

const roundTo = (num: number, decimals = 2) => {
  return Math.round(num * Math.pow(10, decimals)) / Math.pow(10, decimals)
}

const roundToNearestThousand = (num: number) => {
  return num > 0 ? Math.ceil(num / 1000) * 1000 : Math.floor(num / 1000) * 1000
}

const isAfterOrEqual21 = () => {
  const now = dayjs()
  const compareTime = dayjs().hour(21).minute(0).second(0)

  return now.isAfter(compareTime) || now.isSame(compareTime)
}
